import { useMemo } from 'react'
import {
  type CompanyData,
  type CompanyId,
  isEmpty,
  isNotEmpty,
  type ReportKpiFilter,
  type TeamData,
  type TeamId,
  type UserId,
} from '@terros/common'
import { buildTeamOutline, type TeamWithChildren, toTeamOutlineMap, useTeamMembers } from '../team'
import { useRoles } from '../role'
import {
  type DecoratedReportKpiInput,
  type DecoratedReportKpiQuery,
  type DecoratedReportKpiQueryResult,
  type DecoratedReportKpiRow,
  useDecoratedReportKpiV2Queries,
} from '../report'
import { getLevelName } from '../data'
import { getDirectManagerIds, getQuestionManagerReportInput } from './useQuestionReportData'
import { type QuestionManagerBreakdownRow, type QuestionResponseCounts } from './reportTypes'
import { getChildTeams, type QuestionReportScope } from './reportScope'
import { toManagerRows, toTeamCounts, toTeamGrouping, type QuestionReportKpis } from './reportKpis'

type Props = {
  companyId: CompanyId
  company?: CompanyData
  scope: QuestionReportScope
  teams: TeamData[]
  kpis: QuestionReportKpis
  filter: ReportKpiFilter
  expandedTeamIds: TeamId[]
  enabled: boolean
}

export type QuestionBreakdownTreeData = {
  rootTeams: TeamWithChildren[]
  countsByTeamId: Map<TeamId, QuestionResponseCounts>
  managersByTeamId: Map<TeamId, QuestionManagerBreakdownRow[]>
  loadingTeamIds: Set<TeamId>
  scopedManagers?: QuestionManagerBreakdownRow[]
  scopedTeamManagers: QuestionManagerBreakdownRow[]
  scopeManagerCount: number
  companyManagerIds: Set<UserId>
  levelNames: Map<number, string>
  isLoading: boolean
  error?: string
}

export const useQuestionBreakdownTree = ({
  companyId,
  company,
  scope,
  teams,
  kpis,
  filter,
  expandedTeamIds,
  enabled,
}: Props): QuestionBreakdownTreeData => {
  const outline = useMemo(() => buildTeamOutline(teams), [teams])
  const outlineMap = useMemo(() => toTeamOutlineMap(outline), [outline])
  const rootTeams = useMemo(
    () => getChildTeams(scope, teams, company).flatMap((team) => toOutlineNode(team, outlineMap)),
    [scope, teams, company, outlineMap]
  )
  const levelNames = useMemo(() => toLevelNames(teams, company), [teams, company])

  const countQueries = useMemo(
    () => toTeamCountQueries({ rootTeams, expandedTeamIds, outlineMap, kpis, filter, enabled }),
    [rootTeams, expandedTeamIds, outlineMap, kpis, filter, enabled]
  )
  const countStates = useDecoratedReportKpiV2Queries(countQueries)
  const rootCountsState = countStates[0]
  const expandedCountStates = useMemo(() => countStates.slice(1), [countStates])

  const rolesState = useRoles({ companyId, enabled })
  const scopedTeamId = scope.teamId
  const managerTeamIds = useMemo(
    () => (scopedTeamId ? [scopedTeamId, ...expandedTeamIds] : expandedTeamIds),
    [scopedTeamId, expandedTeamIds]
  )
  const teamMembersState = useTeamMembers(managerTeamIds, enabled)
  const managerQueries = useMemo(
    () =>
      managerTeamIds.map((teamId) => {
        const managerIds = getDirectManagerIds(teamMembersState.membersByTeamId.get(teamId), rolesState.data)
        return {
          input: getQuestionManagerReportInput({ kpis: kpis.all, filter: { ...filter, teams: [teamId] }, managerIds }),
          enabled: enabled && isNotEmpty(managerIds),
        }
      }),
    [managerTeamIds, kpis.all, filter, teamMembersState.membersByTeamId, rolesState.data, enabled]
  )
  const managerStates = useDecoratedReportKpiV2Queries(managerQueries)
  const expandedManagerStates = useMemo(
    () => (scopedTeamId ? managerStates.slice(1) : managerStates),
    [scopedTeamId, managerStates]
  )

  const scopeManagerQueries = useMemo(
    (): DecoratedReportKpiQuery[] => [
      { input: { kpis: kpis.all, filter, grouping: { type: 'manager' }, showArchived: true }, enabled },
    ],
    [kpis.all, filter, enabled]
  )
  const [scopeManagerState] = useDecoratedReportKpiV2Queries(scopeManagerQueries)
  const scopeManagers = useMemo(() => toManagerRows(scopeManagerState.data, kpis), [scopeManagerState.data, kpis])
  const companyManagerFilter = useMemo(() => toCompanyManagerFilter(filter), [filter])
  const companyManagerQueries = useMemo(
    (): DecoratedReportKpiQuery[] => [
      {
        input: { kpis: kpis.all, filter: companyManagerFilter, grouping: { type: 'manager' }, showArchived: true },
        enabled,
      },
    ],
    [kpis.all, companyManagerFilter, enabled]
  )
  const [companyManagerState] = useDecoratedReportKpiV2Queries(companyManagerQueries)
  const companyManagerIds = useMemo(
    () => new Set<UserId>(toManagerRows(companyManagerState.data, kpis).map((manager) => manager.id)),
    [companyManagerState.data, kpis]
  )

  const countsByTeamId = useMemo(
    () =>
      toCountsByTeamId(
        countStates.map((state) => state.data),
        kpis
      ),
    [countStates, kpis]
  )
  const managersByTeamId = useMemo(
    () => toManagersByTeamId(managerTeamIds, managerStates, kpis),
    [managerTeamIds, managerStates, kpis]
  )
  const scopedTeamManagers = useMemo(
    () => (scopedTeamId ? (managersByTeamId.get(scopedTeamId) ?? []) : []),
    [scopedTeamId, managersByTeamId]
  )
  const scopedManagers = useMemo(
    () => (scope.managerId ? scopeManagers.filter((manager) => manager.id === scope.managerId) : undefined),
    [scope.managerId, scopeManagers]
  )
  const loadingTeamIds = useMemo(
    () =>
      toLoadingTeamIds({
        teamIds: expandedTeamIds,
        managerStates: expandedManagerStates,
        countStates: expandedCountStates,
        loadingMemberTeamIds: teamMembersState.loadingTeamIds,
        isRolesLoading: rolesState.isLoading,
      }),
    [expandedTeamIds, expandedManagerStates, expandedCountStates, teamMembersState.loadingTeamIds, rolesState.isLoading]
  )
  const hasRootCounts = useMemo(
    () => isEmpty(rootTeams) || rootTeams.some((team) => countsByTeamId.has(team.teamId)),
    [rootTeams, countsByTeamId]
  )

  return {
    rootTeams,
    countsByTeamId,
    managersByTeamId,
    loadingTeamIds,
    scopedManagers,
    scopedTeamManagers,
    scopeManagerCount: scopeManagers.length,
    companyManagerIds,
    levelNames,
    isLoading:
      rootCountsState.isLoading ||
      (rootCountsState.isPlaceholderData && !hasRootCounts) ||
      Boolean(scope.managerId && scopeManagerState.isLoading) ||
      isScopedTeamManagersLoading({
        scopedTeamId,
        loadingMemberTeamIds: teamMembersState.loadingTeamIds,
        isRolesLoading: rolesState.isLoading,
        managerState: scopedTeamId ? managerStates[0] : undefined,
      }),
    error: toQuestionBreakdownError({
      countStates,
      managerStates,
      scopeManagerError: scopeManagerState.error,
      companyManagerError: companyManagerState.error,
      rolesError: rolesState.error,
      teamMembersError: teamMembersState.error,
    }),
  }
}

export const toCompanyManagerFilter = (filter: ReportKpiFilter): ReportKpiFilter => ({
  companyId: filter.companyId,
  startDate: filter.startDate,
  endDate: filter.endDate,
})

type ScopedTeamManagersLoadingProps = {
  scopedTeamId: TeamId | undefined
  loadingMemberTeamIds: Set<TeamId>
  isRolesLoading: boolean
  managerState: DecoratedReportKpiQueryResult | undefined
}

export const isScopedTeamManagersLoading = ({
  scopedTeamId,
  loadingMemberTeamIds,
  isRolesLoading,
  managerState,
}: ScopedTeamManagersLoadingProps): boolean => {
  if (!scopedTeamId) return false
  if (isRolesLoading) return true
  if (loadingMemberTeamIds.has(scopedTeamId)) return true
  return managerState?.isLoading ?? false
}

type QuestionBreakdownErrorProps = {
  countStates: DecoratedReportKpiQueryResult[]
  managerStates: DecoratedReportKpiQueryResult[]
  scopeManagerError?: string
  companyManagerError?: string
  rolesError?: string
  teamMembersError?: string
}

export const toQuestionBreakdownError = ({
  countStates,
  managerStates,
  scopeManagerError,
  companyManagerError,
  rolesError,
  teamMembersError,
}: QuestionBreakdownErrorProps): string | undefined =>
  countStates.find((state) => state.error)?.error ??
  managerStates.find((state) => state.error)?.error ??
  scopeManagerError ??
  companyManagerError ??
  rolesError ??
  teamMembersError

type TeamCountQueriesProps = {
  rootTeams: TeamWithChildren[]
  expandedTeamIds: TeamId[]
  outlineMap: Map<TeamId, TeamWithChildren>
  kpis: QuestionReportKpis
  filter: ReportKpiFilter
  enabled: boolean
}

export const toTeamCountQueries = ({
  rootTeams,
  expandedTeamIds,
  outlineMap,
  kpis,
  filter,
  enabled,
}: TeamCountQueriesProps): DecoratedReportKpiQuery[] => {
  const rootQuery: DecoratedReportKpiQuery = {
    input: toChildTeamCountsInput({ kpis, filter, childTeams: rootTeams }),
    enabled: enabled && isNotEmpty(rootTeams),
  }
  const expandedQueries = expandedTeamIds.map((teamId): DecoratedReportKpiQuery => {
    const childTeams = outlineMap.get(teamId)?.children ?? []
    return {
      input: toChildTeamCountsInput({ kpis, filter: { ...filter, teams: [teamId] }, childTeams }),
      enabled: enabled && isNotEmpty(childTeams),
    }
  })
  return [rootQuery, ...expandedQueries]
}

const toChildTeamCountsInput = ({
  kpis,
  filter,
  childTeams,
}: {
  kpis: QuestionReportKpis
  filter: ReportKpiFilter
  childTeams: TeamData[]
}): DecoratedReportKpiInput => ({
  kpis: kpis.all,
  filter,
  grouping: toTeamGrouping(childTeams[0]?.level, childTeams),
})

const toOutlineNode = (team: TeamData, outlineMap: Map<TeamId, TeamWithChildren>): TeamWithChildren[] => {
  const node = outlineMap.get(team.teamId)
  return node ? [node] : []
}

const toLevelNames = (teams: TeamData[], company: CompanyData | undefined): Map<number, string> => {
  const levelNames = new Map<number, string>()
  teams.forEach((team) => {
    if (levelNames.has(team.level)) return
    levelNames.set(team.level, getLevelName(company, team.level))
  })
  return levelNames
}

const toCountsByTeamId = (
  countRows: (DecoratedReportKpiRow[] | undefined)[],
  kpis: QuestionReportKpis
): Map<TeamId, QuestionResponseCounts> => {
  const countsByTeamId = new Map<TeamId, QuestionResponseCounts>()
  countRows.forEach((rows) => {
    toTeamCounts(rows, kpis).forEach((counts, teamId) => countsByTeamId.set(teamId, counts))
  })
  return countsByTeamId
}

const toManagersByTeamId = (
  teamIds: TeamId[],
  managerStates: DecoratedReportKpiQueryResult[],
  kpis: QuestionReportKpis
): Map<TeamId, QuestionManagerBreakdownRow[]> => {
  const managersByTeamId = new Map<TeamId, QuestionManagerBreakdownRow[]>()
  teamIds.forEach((teamId, index) => {
    const rows = managerStates[index]?.data
    if (!rows) return
    managersByTeamId.set(teamId, toManagerRows(rows, kpis))
  })
  return managersByTeamId
}

type LoadingTeamIdsProps = {
  teamIds: TeamId[]
  managerStates: DecoratedReportKpiQueryResult[]
  countStates: DecoratedReportKpiQueryResult[]
  loadingMemberTeamIds: Set<TeamId>
  isRolesLoading: boolean
}

export const toLoadingTeamIds = ({
  teamIds,
  managerStates,
  countStates,
  loadingMemberTeamIds,
  isRolesLoading,
}: LoadingTeamIdsProps): Set<TeamId> => {
  if (isRolesLoading) return new Set<TeamId>(teamIds)
  const loadingTeamIds = new Set<TeamId>(loadingMemberTeamIds)
  teamIds.forEach((teamId, index) => {
    if (managerStates[index]?.isLoading || countStates[index]?.isLoading) loadingTeamIds.add(teamId)
  })
  return loadingTeamIds
}
